// Draft persistence for the Icon Wizard: in-progress compositions survive
// navigation away from the wizard (sign-in round-trips, Kit creation) and modal
// close/reopen via localStorage. Drafts are keyed — the standalone page uses
// DEFAULT_DRAFT_KEY; the modal keys per seed icon so each icon keeps its own
// work. Versioned so a future layer-shape change can invalidate old drafts
// instead of exploding.
export const DEFAULT_DRAFT_KEY = 'iconBuilder:draft'
const DRAFT_KEY_PREFIX = 'iconBuilder:draft'
export const DRAFT_VERSION = 2
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000
const MAX_ICON_DRAFTS = 20

export function iconDraftKey(iconName, prefix) {
  return `${DRAFT_KEY_PREFIX}:icon:${iconName}:${prefix}`
}

// Two attempts, deliberately. The composition is what must land; the undo
// history is what gives way. Without the second attempt a history that outgrew
// the storage budget would stop the customer's work being saved at all, and the
// catch below would swallow it silently — a worse outcome than having no
// persistent undo. Only the second failure means storage is genuinely gone.
export function persistDraft(key, layers, duotone = false, history = []) {
  try {
    if (!layers || layers.length === 0) {
      localStorage.removeItem(key)
      return
    }
    const base = { v: DRAFT_VERSION, savedAt: Date.now(), layers, duotone }
    // An in-progress gesture is saved closed, not dropped: `layers` already
    // carries its change, so dropping its entry left the last nudge or scale
    // before a reload in the composition with no way to undo it. Closed, because
    // a reload ends the gesture -- the next nudge must start its own entry.
    const saved = (history || []).map((e) => (e.open ? { ...e, open: false } : e))

    try {
      const record = saved.length > 0 ? { ...base, history: saved } : base
      localStorage.setItem(key, JSON.stringify(record))
    } catch (e) {
      localStorage.setItem(key, JSON.stringify(base))
    }
    pruneIconDrafts()
  } catch (e) {
    // localStorage unavailable
  }
}

export function readDraft(key) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const draft = JSON.parse(raw)
    if (draft?.v !== DRAFT_VERSION || !Array.isArray(draft.layers) || draft.layers.length === 0) return null
    if (typeof draft.savedAt !== 'number' || Date.now() - draft.savedAt > DRAFT_TTL_MS) return null
    // `duotone` is read rather than required, and the version is deliberately
    // not bumped for it: a draft written before duotone existed simply has no
    // flag, which reads as off — and its layers have no tone, which reads as
    // primary. Bumping the version would have thrown those drafts away to
    // achieve the same result.
    // `history` is read the same way `duotone` is, and for the same reason: a
    // record written before undo history existed simply has no key, which reads
    // as an empty history. Bumping DRAFT_VERSION would have discarded every
    // draft in the wild to achieve the same result.
    return {
      layers: draft.layers,
      duotone: !!draft.duotone,
      history: Array.isArray(draft.history) ? draft.history : []
    }
  } catch (e) {
    return null
  }
}

export function clearDraft(key) {
  try {
    localStorage.removeItem(key)
  } catch (e) {
    // localStorage unavailable
  }
}

// Drop expired per-icon drafts, then oldest-first down to the cap, so heavy
// wizard use can't crowd localStorage with stale compositions.
function pruneIconDrafts() {
  const iconDrafts = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key || !key.startsWith(`${DRAFT_KEY_PREFIX}:icon:`)) continue
    let savedAt = 0
    try {
      savedAt = JSON.parse(localStorage.getItem(key))?.savedAt || 0
    } catch (e) {
      // unparseable — treat as oldest
    }
    iconDrafts.push({ key, savedAt })
  }
  const now = Date.now()
  const expired = iconDrafts.filter((d) => now - d.savedAt > DRAFT_TTL_MS)
  const live = iconDrafts.filter((d) => now - d.savedAt <= DRAFT_TTL_MS).sort((a, b) => a.savedAt - b.savedAt)
  const overflow = live.slice(0, Math.max(0, live.length - MAX_ICON_DRAFTS))
  for (const { key } of [...expired, ...overflow]) {
    localStorage.removeItem(key)
  }
}
